import { LogoutOutlined, UserOutlined } from '@ant-design/icons';
import { Badge, Divider, theme, Tooltip } from 'antd';
import cn from 'classnames';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import { BUSINESS_SOLUTION_ORGANIZATION_ID } from '@app/access/constants/organizations';
import {
  useLazyGetBranchQuery,
  useLazyGetUserBranchesQuery,
} from '@app/api/Branches';
import { useLazyGetCountriesQuery } from '@app/api/Dictionaries';
import { useGetSidebarCountersQuery } from '@app/api/Sidebar';
import { useSetUserActiveBranchMutation } from '@app/api/User/api';
import { NPage } from '@app/components/Page/types/Page.main';
import {
  findPageByPath,
  getContentStyle,
  hasViewRight,
} from '@app/components/Page/utils';
import { closeRecordModal } from '@app/components/RecordModal/model/recordModalSlice';
import { config } from '@app/config';
import {
  AUTH_SERVER_UNAVAILABLE,
  PATHS_WITH_EXTRA_PAGE_TITLE,
} from '@app/constants';
import {
  ACCESS_RIGHTS_PROP,
  RIGHTS_ANALYTICS,
  RIGHTS_CLIENTS,
  RIGHTS_SALES_REGISTER,
  RIGHTS_IS_EMPLOYEES_REGISTER,
  RIGHTS_IS_JOURNAL,
  RIGHTS_IS_ON,
  RIGHTS_IS_REGISTER,
  RIGHTS_LEADS,
  RIGHTS_RECORDS,
  RIGHTS_SALES_FUNNELS,
  RIGHTS_CRM_TASKS,
  RIGHTS_USERS,
} from '@app/constants/accessRights';
import { FRONT_WARNING, WARNING } from '@app/constants/alerts';
import {
  ANALYTICS,
  ANALYTICS_PATH,
  AUTHORIZATION_PATH,
  CALENDAR,
  CALENDAR_PATH,
  CLIENT_PAYMENTS,
  CLIENTS,
  CONTROL_PANEL,
  EDIT_CLIENT_PATH,
  EDIT_EMPLOYEE_PATH,
  EMPLOYEES,
  INVENTORY,
  INVENTORY_CARD_PATH,
  LEAD_CARD_PATH,
  LEADS,
  LEADS_PATH,
  ORGANIZATION_AI_SETTINGS_KEY,
  ORGANIZATIONS,
  ORGANIZATIONS_KEY,
  PRODUCTS,
  PROFILE_KEY,
  PROFILE_PATH,
  RECORDS,
  SALES_FUNNELS,
  SENDOUT_RECEIVERS_PATH,
  SENDOUTS,
  SETTINGS,
  TASK_LIST,
  TASK_LIST_PATH,
  VIEW_SENDOUTS_PATH,
  WAREHOUSES,
  SETTINGS_KEY,
  REGISTRATION_PATH,
  BILLING,
  YOO_KASSA_SUCCESS_PAYMENT_PATH,
  YOO_KASSA_PAYMENT_WITH_EMAIL_PATH,
} from '@app/constants/navigation';
import { navigation } from '@app/data';
import { APP_VERSION_TIMESTAMP } from '@app/data/version';
import { useBugReport } from '@app/features/BugReport';
import {
  useAccessToOrganizations,
  useNavigatePreserveQueryParams,
  useNewUserAccessRights,
  useRightsToSendouts,
  useUserDelete,
  useUserPrint,
  useWindowSize,
} from '@app/hooks';
import { getAnalyticsReportTitle } from '@app/pages/Analytics/utils';
import { adaptUserToRedux } from '@app/pages/Authorization';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import {
  LOYALTY_ROOT,
  SEGMENTS,
  LOYALTY,
  CARDS,
  RECOMMEND_APP,
  RECOMMEND_APP_PARTNERS,
  RECOMMEND_SETTINGS,
  BOOKING,
} from '@app/routes/iframe-routes/constants';
import { NEW_CLIENTS } from '@app/routes/pages/clientsPages/constants';
import {} from '@app/routes/pages/leadsPages/constants';
import { authenticateByCookie, handleCatchError } from '@app/services';
import { serverStatus } from '@app/services/__global__/serverStatus';
import {
  isSessionRevalidated,
  markSessionRevalidated,
} from '@app/services/authentication/sessionRevalidation';
import { useTodayMoment } from '@app/shared/hooks';
import {
  generateSpanWithDataTestId,
  getLatestFrontendVersionTimestamp,
  isFrontendUpdateAvailable,
  isFrontendVersionOutdated,
  LATEST_FRONTEND_VERSION_TIMESTAMP_STORAGE_KEY,
  RELOADED_FRONTEND_VERSION_TIMESTAMP_STORAGE_KEY,
  toUnixSeconds,
} from '@app/shared/utils';
import {
  selectMenuSidebarState,
  selectRoutes,
  selectUser,
  setAccessRights,
  setActiveBranchCountryId,
  setActiveBranchName,
  setCountries,
  setMenuSidebarState,
  setOrganizations,
  setRoutes,
  setUser,
  useAppDispatch,
  useAppSelector,
} from '@app/store';
import type { NNavigation } from '@app/types/Navigation';
import {
  createWSConnection,
  findSidebarParentKey,
  getAccessToken,
  hasToken,
  removeAccessToken,
  setActiveBranchSession,
} from '@app/utils';
import {
  invokeWSMethod,
  subscribeToWsEvent,
} from '@app/utils/createWSConnection';
import { hasModuleRight } from '@app/utils/hasModuleRight';

import {
  consumeMcpLoginReturn,
  rememberMcpLoginReturn,
} from '@pages/McpIntegration/utils/loginReturn';

import { filterWarehouseChildren } from '../utils';

import { useDocumentTitle } from './useDocumentTitle';
import { useLeadsSectionMemory } from './useLeadsSectionMemory';

import type { MenuProps } from 'antd/lib';
import type { Dayjs } from 'dayjs';
import type {
  MouseEvent as ReactMouseEvent,
  KeyboardEvent as ReactKeyboardEvent,
} from 'react';

type MenuDomEvent =
  | ReactMouseEvent<HTMLElement>
  | ReactKeyboardEvent<HTMLElement>;

interface PageLocationState {
  goBackRoute?: string;
}

const isReactMouseEvent = (
  e: MenuDomEvent
): e is ReactMouseEvent<HTMLElement> => 'button' in e;

// Пункт «Лиды» в меню один и ведёт в раздел, открытый последним
// (useLeadsSectionMemory). И клик, и ссылка пункта должны узнавать его
// одинаково, иначе переходы по ним разъезжаются.
const isLeadsMenuEntry = (item: NNavigation.NavigationAllItems): boolean =>
  item.sidebarparentid === LEADS && item.id === LEADS;

const isPageLocationState = (state: unknown): state is PageLocationState => {
  if (typeof state !== 'object' || state === null) return false;
  if (!('goBackRoute' in state)) return true;

  return typeof state.goBackRoute === 'string';
};

const defaultTooltip = 'Пункт навигации недоступен';
const { useToken } = theme;

interface Props {
  secondLevelData?: NPage.LevelDataModel;
  thirdLevelData?: NPage.LevelDataModel;
  goBackRoute?: string;
  disableBreadcrumbs?: boolean;
  disableContentStyles?: boolean;
}

export const usePage = ({
  secondLevelData,
  thirdLevelData,
  goBackRoute,
  disableBreadcrumbs,
  disableContentStyles,
}: Props) => {
  const [isAuthChecking, setIsAuthChecking] = useState(() => !hasToken());
  const [isSessionCheckPostponed, setIsSessionCheckPostponed] = useState(false);
  const authInFlightRef = useRef(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname, search } = location;
  const locationState = isPageLocationState(location.state)
    ? location.state
    : null;
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    token: { colorBgContainer },
  } = useToken();

  const { showNotification } = useNotification();
  const [titleData, setTitleData] = useState<string | JSX.Element>('');
  const user = useAppSelector(selectUser);
  const { photo, nameFirst, nameLast } = user;

  const {
    userId,
    database,
    activeBranchId,
    activeBranchName,
    activeBranchCountryId,
  } = useUserPrint();
  const { innerWidth } = useWindowSize();
  const isMobile = innerWidth <= 600;

  const { data: sidebarCounters, refetch: refetchSidebarCounters } =
    useGetSidebarCountersQuery(
      { ActiveBranchId: activeBranchId ?? undefined },
      { pollingInterval: 60000, skip: !activeBranchId }
    );
  const [getBranch] = useLazyGetBranchQuery();
  const [getUserBranches] = useLazyGetUserBranchesQuery();
  const [setUserActiveBranch] = useSetUserActiveBranchMutation();
  const [getCountries] = useLazyGetCountriesQuery();

  useEffect(() => {
    if (
      !activeBranchId ||
      (!pathname.startsWith(LEADS_PATH) && !pathname.startsWith(TASK_LIST_PATH))
    )
      return;
    refetchSidebarCounters();
  }, [pathname, activeBranchId, refetchSidebarCounters]);

  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [isBranchSelectModalOpen, setBranchSelectModal] = useState(false);
  const [isCalendarDialogOpen, setIsCalendarDialogOpen] = useState(false);
  const [isHelpBoxOpened, setIsHelpBoxOpened] = useState(false);
  const resolvePageKeyByPath = (path: string): string => {
    const pageMeta = findPageByPath(
      navigation as NNavigation.NavigationAllItems[],
      path
    );

    if (!pageMeta?.key) {
      return SETTINGS_KEY;
    }

    return findSidebarParentKey(pageMeta.key);
  };

  const [pageKey, setPageKey] = useState(() => resolvePageKeyByPath(pathname));
  useEffect(() => {
    setPageKey(resolvePageKeyByPath(pathname));
  }, [pathname]);

  const { userAccessRights, organizationAccessRights } =
    useNewUserAccessRights();

  const { isAccessToSendoutsRegistry } = useRightsToSendouts();
  const { areRightsToOrganizationsModule } = useAccessToOrganizations();
  const { navigatePreserveQueryParams } = useNavigatePreserveQueryParams([
    'clientId',
  ]);
  useUserDelete();

  const menuSidebarState = useSelector(selectMenuSidebarState);

  const isSmall = menuSidebarState === NPage.MenuSidebarState.SMALL;
  const isFull = menuSidebarState === NPage.MenuSidebarState.FULL;
  const isNone = menuSidebarState === NPage.MenuSidebarState.NONE;
  const hideMenuSidebar = isMobile;
  const currentPageUrl = pathname + search;
  const isClientEditionPage = currentPageUrl.includes(EDIT_CLIENT_PATH);
  const isLeadsServiceAsSource = currentPageUrl.includes('from=leads');
  const leadId = searchParams.get('leadId');

  const accessToken = getAccessToken();

  const isAuth = useMemo(() => {
    return Boolean(accessToken);
  }, [accessToken]);

  const isLoadingAccessRights = useAppSelector(
    ({ newAccessRights }) => newAccessRights.isLoading
  );

  const isRehydrated = useAppSelector(
    (state) => state._persist?.rehydrated ?? false
  );

  // Публичные страницы ЮKassa открывает клиент без входа в CRM — ни проверка
  // сессии, ни права доступа их не касаются. Если в браузере есть cookie CRM,
  // токен может появиться уже после загрузки (authenticateByCookie из
  // customBaseQuery), и без этого исключения страница уходила в AppLoader:
  // isAuth становился true, а isAuthChecking так и оставался true, потому что
  // authenticateHandler для публичных страниц выходит раньше (issue/11113).
  const isPublicPage = useMemo(
    () =>
      pathname.startsWith(YOO_KASSA_SUCCESS_PAYMENT_PATH) ||
      pathname.startsWith(YOO_KASSA_PAYMENT_WITH_EMAIL_PATH),
    [pathname]
  );

  const isLoadingPage = useMemo(() => {
    if (isPublicPage) return false;

    const isForbiddenPage =
      !isAuth &&
      !pathname.startsWith(AUTHORIZATION_PATH) &&
      !pathname.startsWith(REGISTRATION_PATH);

    // Пока идёт проверка сессии по cookie, залогиненному пользователю не
    // показываются данные: удалённый не должен успеть увидеть часть
    // интерфейса до редиректа на авторизацию (issue/11055). На страницах без
    // токена проверка идёт фоном и форму входа не перекрывает.
    return isAuth ? isAuthChecking || isLoadingAccessRights : isForbiddenPage;
  }, [isLoadingAccessRights, isAuth, isAuthChecking, isPublicPage, pathname]);

  const [isNewAppVersion, setIsNewAppVersion] = useState(false);

  const updateFrontendVersionState = useCallback(
    (latestVersionTimestamp: number | null) => {
      const isOutdated = isFrontendVersionOutdated(
        latestVersionTimestamp,
        APP_VERSION_TIMESTAMP
      );

      setIsNewAppVersion(
        isFrontendUpdateAvailable(
          latestVersionTimestamp,
          APP_VERSION_TIMESTAMP,
          toUnixSeconds(
            sessionStorage.getItem(
              RELOADED_FRONTEND_VERSION_TIMESTAMP_STORAGE_KEY
            )
          )
        )
      );

      if (isOutdated && latestVersionTimestamp) {
        sessionStorage.setItem(
          LATEST_FRONTEND_VERSION_TIMESTAMP_STORAGE_KEY,
          String(latestVersionTimestamp)
        );
        return;
      }

      sessionStorage.removeItem(LATEST_FRONTEND_VERSION_TIMESTAMP_STORAGE_KEY);
    },
    []
  );

  const reloadAppWithCurrentUrl = useCallback(() => {
    // Запоминаем версию, ради которой перезагружаемся: если стенд отдаст тот же
    // бандл, баннер для неё больше не покажется (issue/11217).
    const latestVersionTimestamp = sessionStorage.getItem(
      LATEST_FRONTEND_VERSION_TIMESTAMP_STORAGE_KEY
    );

    if (latestVersionTimestamp) {
      sessionStorage.setItem(
        RELOADED_FRONTEND_VERSION_TIMESTAMP_STORAGE_KEY,
        latestVersionTimestamp
      );
    }

    const url = new URL(window.location.href);
    url.searchParams.set('_', Date.now().toString());
    window.location.replace(url.toString());
  }, []);

  useEffect(() => {
    const latestVersionTimestamp = toUnixSeconds(
      sessionStorage.getItem(LATEST_FRONTEND_VERSION_TIMESTAMP_STORAGE_KEY)
    );

    updateFrontendVersionState(latestVersionTimestamp);
  }, [updateFrontendVersionState]);

  const pageMeta = findPageByPath(
    navigation as NNavigation.NavigationAllItems[],
    pathname
  );

  const { openCreateBugReportModal, renderCreateBugReportModal } = useBugReport(
    { setIsHelpBoxOpened }
  );

  const isPageWithExtraTitle = PATHS_WITH_EXTRA_PAGE_TITLE.includes(pathname);

  const pageBreadcrumbDataTestId = pageMeta?.pagebreadcrumbdatatestid || '';
  const pageTitleDataTestId = pageMeta?.pagetitledatatestid || '';
  const { inventoryId } = useParams<{ inventoryId: string }>();
  const routes = useAppSelector(selectRoutes);

  const getPageTitle = useCallback(() => {
    if (pathname === ANALYTICS_PATH) {
      return `Аналитика: ${getAnalyticsReportTitle(searchParams.get('tab'))}`;
    } else if (pageMeta?.path === INVENTORY_CARD_PATH) {
      return `Инвентаризация №${inventoryId}`;
    } else {
      return pageMeta?.label || '';
    }
  }, [pageMeta, pathname, searchParams, inventoryId]);

  const pageTitle = getPageTitle();

  useDocumentTitle({ pageTitle });

  const { leadsSectionPath } = useLeadsSectionMemory(pathname);

  const secretFromParam = String(searchParams.get('secret') || '');

  const authenticateHandler = useCallback(async () => {
    if (isPublicPage) {
      setIsAuthChecking(false);
      return;
    }

    if (hasToken()) {
      const hasIdentity = Boolean(userId && database && activeBranchId);

      // redux-persist has not finished restoring yet: the identity may still
      // appear, so nothing is decided here. The effect re-runs on isRehydrated.
      // Without this the access-rights request can never start and the app
      // hangs on AppLoader until the tab is closed.
      if (!isRehydrated) {
        setIsAuthChecking(false);
        return;
      }

      // Личность восстановлена и сессия уже проверена на бэке в эту загрузку
      // страницы — это обычная навигация внутри приложения, повторный запрос
      // не нужен. Первый заход в загрузку страницы всегда проходит проверку
      // ниже: наличия токена недостаточно, пользователя могли удалить, пока
      // вкладка была закрыта (issue/11055). Токен без личности после
      // рехидрации — осиротевший (logout/login или гонка redux-persist между
      // вкладками) и тоже требует повторной авторизации по cookie.
      if (hasIdentity && isSessionRevalidated()) {
        setIsAuthChecking(false);
        return;
      }
    }

    if (authInFlightRef.current) return;
    authInFlightRef.current = true;

    setIsAuthChecking(true);

    // Пока бэкенд недоступен, сессия не проверена, но и не отклонена: токен и
    // адрес остаются, проверку повторяет опрос из usePageEffects (issue/11376).
    let isServerUnavailable = false;

    try {
      const data = await authenticateByCookie();

      if (data === AUTH_SERVER_UNAVAILABLE) {
        isServerUnavailable = true;
        // Интерцептор axios включает баннер только после 3 сбоев подряд
        // (кроме сетевой ошибки), а при отложенной проверке сессии он нужен
        // сразу: пользователь видит только AppLoader.
        serverStatus.recordCriticalFailure();
        return;
      }

      // 'navigation' also covers any other failed cookie auth (500 with JSON,
      // 401, unparsable answer) — it lands on the login page instead of
      // leaving the user stuck on AppLoader with no token and no retry.
      if (data === 'navigation') {
        if (secretFromParam) return;
        // Drop any stale/orphaned access token so isAuth flips to false and the
        // login page is shown instead of the app loader. No-op when absent.
        removeAccessToken();
        rememberMcpLoginReturn(pathname + window.location.search);
        navigate(
          pathname === REGISTRATION_PATH
            ? REGISTRATION_PATH
            : AUTHORIZATION_PATH
        );
      } else {
        dispatch(setUser(adaptUserToRedux(data)));
        dispatch(setAccessRights(data[ACCESS_RIGHTS_PROP]));
        dispatch(setOrganizations(data.Organizations));
      }
    } catch (error) {
      showNotification(handleCatchError(error), WARNING);
    } finally {
      authInFlightRef.current = false;
      setIsSessionCheckPostponed(isServerUnavailable);

      // Пока бэкенд недоступен, раздел не монтируется: остаётся AppLoader с
      // баннером (баннер включён выше, при AUTH_SERVER_UNAVAILABLE). Раздел
      // смонтируется и загрузит данные сам после успешного повтора проверки.
      if (!isServerUnavailable) {
        // Проверка на бэке состоялась (успешно или нет) — в эту загрузку
        // страницы больше не повторяем, иначе запрос уходил бы на каждую
        // навигацию.
        markSessionRevalidated();
        setIsAuthChecking(false);
      }
    }
  }, [
    isPublicPage,
    pathname,
    secretFromParam,
    navigate,
    dispatch,
    showNotification,
    userId,
    database,
    activeBranchId,
    isRehydrated,
  ]);

  const lastResolvedBranchIdRef = useRef<number | null>(null);
  const branchRecoveryAttemptedRef = useRef(false);

  // Сохранённый в сессии вкладки филиал может стать невалидным: пользователя
  // убрали из филиала или филиал удалили — GetBranch по нему падает. Один раз
  // за жизнь вкладки пробуем заменить его первым доступным из списка филиалов
  // пользователя; если сохранённый филиал в списке есть (ошибка транзиентная)
  // или списка нет — восстановление не выполняется и остаётся обычная
  // нотификация об ошибке.
  const recoverInvalidActiveBranch = useCallback(async (): Promise<boolean> => {
    if (branchRecoveryAttemptedRef.current) return false;
    branchRecoveryAttemptedRef.current = true;

    try {
      const availableBranches = await getUserBranches({
        ActiveBranchId: activeBranchId,
      }).unwrap();

      const isStoredBranchAvailable = availableBranches.some(
        (branch) => branch.BranchId === activeBranchId
      );
      if (isStoredBranchAvailable || !availableBranches.length) return false;

      const [replacementBranch] = availableBranches;
      const replacementName = replacementBranch.Name || 'Филиал без названия';

      lastResolvedBranchIdRef.current = replacementBranch.BranchId;
      setActiveBranchSession({
        activeBranchId: replacementBranch.BranchId,
        activeBranchName: replacementName,
        activeBranchCountryId: replacementBranch.CountryId,
        activeBranchUserId: userId,
      });
      dispatch(
        setUser({ ...user, activeBranchId: replacementBranch.BranchId })
      );
      dispatch(setActiveBranchName(replacementName));
      dispatch(setActiveBranchCountryId(replacementBranch.CountryId));

      try {
        await setUserActiveBranch({
          ActiveBranchId: replacementBranch.BranchId,
          branchId: replacementBranch.BranchId,
        }).unwrap();
      } catch (persistError) {
        showNotification(handleCatchError(persistError), WARNING);
      }

      return true;
    } catch {
      // Список филиалов получить не удалось — восстановление невозможно,
      // выше покажется нотификация об исходной ошибке резолва филиала.
      return false;
    }
  }, [
    activeBranchId,
    userId,
    user,
    dispatch,
    getUserBranches,
    setUserActiveBranch,
    showNotification,
  ]);

  const fetchBranchLightHandler = useCallback(async () => {
    if (!activeBranchId || !accessToken) return;

    // Reconcile the displayed branch name/country to the authoritative
    // activeBranchId. Re-resolve not only when the name/country is missing, but
    // also whenever the active branch id itself changed. Otherwise a stale name
    // left over from a previously selected branch keeps showing in the header
    // while the branch list highlights the real active branch — the reported
    // desync. Single source of truth for the active branch is activeBranchId.
    const isAlreadyResolved =
      lastResolvedBranchIdRef.current === activeBranchId &&
      !!activeBranchName &&
      !!activeBranchCountryId;

    if (isAlreadyResolved) return;

    try {
      const { Name, CountryId } = await getBranch({
        ActiveBranchId: activeBranchId,
        branchId: activeBranchId,
      }).unwrap();

      lastResolvedBranchIdRef.current = activeBranchId;
      dispatch(setActiveBranchName(Name));
      dispatch(setActiveBranchCountryId(CountryId));
      // useUserPrint reads the session before Redux, so keep the session name/
      // country in sync with the resolved branch. The session id is left
      // untouched — only the display name/country are reconciled to it.
      setActiveBranchSession({
        activeBranchName: Name,
        activeBranchCountryId: CountryId,
      });
    } catch (error) {
      const isRecovered = await recoverInvalidActiveBranch();
      if (!isRecovered) {
        showNotification(handleCatchError(error), WARNING);
      }
    }
  }, [
    activeBranchId,
    accessToken,
    activeBranchName,
    activeBranchCountryId,
    getBranch,
    dispatch,
    showNotification,
    recoverInvalidActiveBranch,
  ]);

  const goBackWithCustomization = useCallback(() => {
    if (isClientEditionPage && isLeadsServiceAsSource) {
      const isRecordModal = search.includes('source=client');

      if (isRecordModal) {
        dispatch(closeRecordModal());

        searchParams.delete('source');

        return navigate(
          {
            pathname,
            search: searchParams.toString(),
          },
          { replace: true }
        );
      }

      if (!leadId) {
        return showNotification('Идентификатор лида не найден', FRONT_WARNING);
      }

      return navigate(`${LEAD_CARD_PATH}?leadId=${leadId}`);
    }

    navigate(-1);
  }, [
    isClientEditionPage,
    isLeadsServiceAsSource,
    navigate,
    search,
    pathname,
    leadId,
    dispatch,
    searchParams,
    showNotification,
  ]);

  const findPageByKey = useCallback(
    (
      items: NNavigation.NavigationAllItems[],
      key: string
    ): NNavigation.NavigationAllItems | undefined => {
      for (const item of items) {
        if (item.key === key) return item;
        if (item.children) {
          const found = findPageByKey(
            item.children as NNavigation.NavigationAllItems[],
            key
          );
          if (found) return found;
        }
      }
      return undefined;
    },
    []
  );

  const navigateToPage = useCallback(
    (key: string) => {
      const currentPage = findPageByKey(
        navigation as NNavigation.NavigationAllItems[],
        key
      );

      if (!currentPage?.path) {
        return showNotification(
          'Ссылка для перехода не определена',
          FRONT_WARNING
        );
      }
      // Клик идёт по key и берёт путь из исходной navigation, где у «Лидов»
      // статический /leads. Запомненный раздел живёт отдельно, поэтому его
      // нужно подставить здесь же — иначе клик всегда открывает реестр.
      let path = isLeadsMenuEntry(currentPage)
        ? leadsSectionPath
        : currentPage.relativepath || currentPage.path;

      // Разделы-iframe объявлены путями со звёздочкой (`/online-booking/*`,
      // `/loyalty/*`, `/recommend/*`). Открывать их нужно с чистого адреса
      // раздела: query предыдущей страницы (например, `leadId` и `source`
      // карточки лида) к разделу отношения не имеет (issue/11064).
      if (path.endsWith('/*')) {
        path = path.slice(0, -2);
      }

      navigate(path);
    },
    [navigate, showNotification, findPageByKey, leadsSectionPath]
  );

  const resolveMenuHref = useCallback(
    (item: NNavigation.NavigationAllItems): string | null => {
      const rawPath = item.relativepath || item.path;
      if (!rawPath || typeof rawPath !== 'string') return null;
      if (rawPath.endsWith('/*')) {
        return rawPath.slice(0, -2);
      }

      return rawPath;
    },
    []
  );

  const withMenuLinks = useCallback(
    (
      items: NNavigation.NavigationAllItems[]
    ): NNavigation.NavigationAllItems[] => {
      return items.map((item) => {
        const children = item.children as
          | NNavigation.NavigationAllItems[]
          | undefined;

        const hasChildren = Boolean(children?.length);

        if (hasChildren) {
          return {
            ...item,
            children: withMenuLinks(children!),
          };
        }

        const href = resolveMenuHref(item);
        if (!href) return item;

        return {
          ...item,
          label: (
            <a className="sidebar-menu-link" href={href}>
              {item.label}
            </a>
          ),
        };
      });
    },
    [resolveMenuHref]
  );

  const withCounterBadges = useCallback(
    (
      items: NNavigation.NavigationAllItems[]
    ): NNavigation.NavigationAllItems[] =>
      items.map((item) => {
        const leadsCount = sidebarCounters?.CountLeads;
        const tasksCount = sidebarCounters?.CountCrmTasks;

        if (item.id === LEADS) {
          if (isSmall) {
            return {
              ...item,
              icon: (
                <Badge
                  count={leadsCount}
                  size="small"
                  className="badge-over-icon"
                  dot
                  offset={[6, 0]}
                >
                  {item.icon}
                </Badge>
              ),
            };
          }
          return {
            ...item,
            label: (
              <span className="sidebar-menu-item-with-badge">
                {item.label}
                <Badge count={leadsCount} overflowCount={99} />
              </span>
            ),
          };
        }

        if (item.id === TASK_LIST) {
          if (isSmall) {
            return {
              ...item,
              icon: (
                <Badge
                  count={tasksCount}
                  size="small"
                  className="badge-over-icon"
                  dot
                  offset={[6, 0]}
                >
                  {item.icon}
                </Badge>
              ),
            };
          }
          return {
            ...item,
            label: (
              <span className="sidebar-menu-item-with-badge">
                {item.label}
                <Badge count={tasksCount} overflowCount={99} />
              </span>
            ),
          };
        }

        return item;
      }),
    [sidebarCounters, isSmall]
  );

  // «Лиды» — один пункт без подменю: разделы «Реестр» и «Воронки» переключает
  // тумблер в шапке самой страницы, дублировать его в меню значит добавлять
  // лишний клик на каждый вход. Пункт ведёт в раздел, открытый последним.
  const withLeadsSectionPath = useCallback(
    (
      items: NNavigation.NavigationAllItems[]
    ): NNavigation.NavigationAllItems[] =>
      items.map((item) => {
        if (!isLeadsMenuEntry(item)) return item;

        return { ...item, path: leadsSectionPath };
      }),
    [leadsSectionPath]
  );

  const withOrganizationsSubmenu = useCallback(
    (
      items: NNavigation.NavigationAllItems[]
    ): NNavigation.NavigationAllItems[] => {
      const organizationAiSettingsPage = findPageByKey(
        navigation as NNavigation.NavigationAllItems[],
        ORGANIZATION_AI_SETTINGS_KEY
      );

      return items.map((item) => {
        const isOrganizationsListPage =
          item.sidebarparentid === ORGANIZATIONS && item.id === ORGANIZATIONS;

        if (!isOrganizationsListPage || !organizationAiSettingsPage) {
          return item;
        }

        return {
          ...item,
          key: `${ORGANIZATIONS_KEY}-menu`,
          className: [item.className, 'menu-item-with-children']
            .filter(Boolean)
            .join(' '),
          children: [
            {
              ...item,
              children: undefined,
            },
            organizationAiSettingsPage,
          ],
        };
      });
    },
    [findPageByKey]
  );

  const navigateToProfile = () => {
    setPageKey(PROFILE_KEY);
    navigate('/profile');
  };

  const showModal = () => {
    setIsLogoutModalOpen(true);
  };

  useEffect(() => {
    if (searchParams.get('_')) {
      setTimeout(() => {
        searchParams.delete('_');
        setSearchParams(searchParams);
      }, 100);
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (isMobile && !isNone) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobile, isNone]);

  const memoizedNavigation = useMemo(() => {
    if (!userAccessRights) return;

    const enabledModules = userAccessRights?.Modules;
    if (!enabledModules) return;

    const filteredNavigation = (
      navigation as NNavigation.NavigationAllItems[]
    ).filter((item) => {
      const { id, sidebarparentid, issidebaritem }: NNavigation.NavigationItem =
        item;

      const isOrganizationsListPage =
        sidebarparentid === ORGANIZATIONS && id === ORGANIZATIONS;
      const isSendoutsPage = sidebarparentid === SENDOUTS && id === SENDOUTS;
      const isBillingPage = sidebarparentid === BILLING && id === BILLING;

      if (isBillingPage) {
        return true;
      }

      if (isOrganizationsListPage) {
        if (config.mode === 'production') {
          return database === BUSINESS_SOLUTION_ORGANIZATION_ID;
        } else {
          return areRightsToOrganizationsModule;
        }
      }

      if (isSendoutsPage) {
        return isAccessToSendoutsRegistry;
      }

      const { Warehouse, Products } = userAccessRights;

      const warehouseModuleAllowed =
        organizationAccessRights.find(({ RightType }) => RightType === 9)
          ?.Allowed || false;
      const isWarehousePages =
        sidebarparentid === PRODUCTS ||
        sidebarparentid === INVENTORY ||
        sidebarparentid === WAREHOUSES;

      // Раздел «Склад» показываем и с одним правом «Товары»: в нём лежит
      // «Справочник товаров», который раньше был в настройках и требовал только
      // Products.IsOn. Остальные пункты раздела отсекает filterWarehouseChildren.
      if (id === WAREHOUSES && sidebarparentid === WAREHOUSES) {
        return (
          warehouseModuleAllowed &&
          hasModuleRight(item.servicerightsid, enabledModules) &&
          (Warehouse?.IsOn === true || Products?.IsOn === true)
        );
      }

      if (isWarehousePages && (!Warehouse?.IsOn || !warehouseModuleAllowed)) {
        return false;
      }

      const isLoyaltyPages =
        sidebarparentid === LOYALTY_ROOT ||
        sidebarparentid === SEGMENTS ||
        sidebarparentid === LOYALTY ||
        sidebarparentid === CARDS ||
        sidebarparentid === RECOMMEND_APP ||
        sidebarparentid === RECOMMEND_APP_PARTNERS ||
        sidebarparentid === RECOMMEND_SETTINGS;

      // Видимость пункта сайдбара «Лояльность» управляется правом
      // ModelAccessRights.Loyalty.IsOn (us/11069). Доступ организации к модулю
      // бэк уже учитывает в этом флаге — отдельного модуля лояльности в
      // GetModulesRights нет (в отличие от склада), поэтому AND по
      // organizationAccessRights прятал пункт даже при выданном праве. Явный
      // return нужен, иначе пункт (нет servicerightsid и записи в rightsMap)
      // отваливается из фильтра для не-админов.
      if (isLoyaltyPages) {
        return userAccessRights.Loyalty?.IsOn === true;
      }

      // Видимость пункта сайдбара «Онлайн запись» управляется правом
      // ModelAccessRights.OnlineRecording.IsOn (us/11069).
      if (sidebarparentid === BOOKING) {
        return userAccessRights.OnlineRecording?.IsOn === true;
      }

      if (
        item.sidebarparentid === CONTROL_PANEL &&
        !userAccessRights.IsSuperAdmin
      ) {
        return false;
      }

      if (
        userAccessRights.IsSuperAdmin &&
        issidebaritem !== 'false' &&
        hasModuleRight(item.servicerightsid, enabledModules)
      )
        return true;

      if (
        userAccessRights.IsAdmin &&
        !userAccessRights.IsSuperAdmin &&
        issidebaritem !== 'false' &&
        hasModuleRight(item.servicerightsid, enabledModules)
      )
        return true;

      if (!hasModuleRight(item.servicerightsid, enabledModules)) return false;

      const hasRights =
        item?.servicerightsid &&
        userAccessRights[item.servicerightsid]?.IsOn === true;
      const hasRightToJournal =
        userAccessRights?.[RIGHTS_RECORDS]?.[RIGHTS_IS_JOURNAL] === true;
      const hasRightToRecords =
        userAccessRights?.[RIGHTS_RECORDS]?.[RIGHTS_IS_REGISTER] === true;
      const hasRightToAnalytics =
        userAccessRights?.[RIGHTS_ANALYTICS]?.[RIGHTS_IS_ON] === true;
      const hasRightToClientsRegistry =
        userAccessRights?.[RIGHTS_CLIENTS]?.[RIGHTS_IS_ON] === true;
      const hasRightToFinancialOperations =
        userAccessRights?.[RIGHTS_SALES_REGISTER]?.[RIGHTS_IS_ON] === true;
      const hasRightToEmployeesRegistry =
        userAccessRights?.[RIGHTS_USERS]?.[RIGHTS_IS_EMPLOYEES_REGISTER] ===
        true;
      const hasRightToLeads =
        userAccessRights?.[RIGHTS_LEADS]?.[RIGHTS_IS_ON] === true;
      const hasRightsToTasks =
        userAccessRights?.[RIGHTS_CRM_TASKS]?.[RIGHTS_IS_ON] === true;
      const hasRightToSalesFunnels =
        userAccessRights?.[RIGHTS_SALES_FUNNELS]?.[RIGHTS_IS_ON] === true;

      // Нужно оставить без CamelCase, иначе Реакт ругается
      if (issidebaritem !== 'false' && item.sidebarparentid !== CONTROL_PANEL) {
        const rightsMap = {
          [CALENDAR]: hasRightToJournal,
          [RECORDS]: hasRightToRecords,
          [ANALYTICS]: hasRightToAnalytics,
          [CLIENTS]: hasRightToClientsRegistry,
          [NEW_CLIENTS]: hasRightToClientsRegistry,
          [EMPLOYEES]: hasRightToEmployeesRegistry,
          [CLIENT_PAYMENTS]: hasRightToFinancialOperations,
          [LEADS]: hasRightToLeads,
          [TASK_LIST]: hasRightsToTasks,
          [SALES_FUNNELS]: hasRightToSalesFunnels,
          [WAREHOUSES]: Warehouse.IsOn && warehouseModuleAllowed,
          [PRODUCTS]: Products.IsOn,
          [BILLING]: true,
        };

        if (item.id === SETTINGS) {
          return true;
        }

        if (
          hasRights &&
          (rightsMap[item.id as keyof typeof rightsMap] ||
            !item?.servicerightsid)
        ) {
          return true;
        }
      }
    });

    const navigationWithWarehouseChildren = filterWarehouseChildren(
      filteredNavigation,
      {
        canViewWarehouse: userAccessRights.Warehouse?.IsOn === true,
        canViewProducts: userAccessRights.Products?.IsOn === true,
      }
    );

    return withMenuLinks(
      withOrganizationsSubmenu(
        withLeadsSectionPath(withCounterBadges(navigationWithWarehouseChildren))
      )
    );
  }, [
    userAccessRights,
    organizationAccessRights,
    database,
    areRightsToOrganizationsModule,
    isAccessToSendoutsRegistry,
    withMenuLinks,
    withCounterBadges,
    withLeadsSectionPath,
    withOrganizationsSubmenu,
  ]);

  const goBack = useCallback(() => {
    goBackWithCustomization();
  }, [goBackWithCustomization]);

  const homePageRedirect = useCallback(() => {
    if (
      isAuth &&
      userAccessRights.UserId &&
      !isPublicPage &&
      !isAuthChecking &&
      !pathname.startsWith(AUTHORIZATION_PATH)
    ) {
      const returnPath = consumeMcpLoginReturn();
      if (returnPath) {
        navigate(returnPath, { replace: true });
        return;
      }
    }
    if (userAccessRights.UserId && userAccessRights && pathname === '/') {
      const path = userAccessRights?.Records?.IsJournal
        ? CALENDAR_PATH
        : PROFILE_PATH;
      navigate(path);
    }
  }, [
    userAccessRights,
    navigate,
    pathname,
    isAuth,
    isPublicPage,
    isAuthChecking,
  ]);

  const getCountriesHandler = useCallback(async () => {
    try {
      if (userId && database && activeBranchId) {
        const response = await getCountries({
          ActiveBranchId: activeBranchId,
        }).unwrap();
        dispatch(setCountries({ data: response }));
      }
    } catch (error) {
      showNotification(error as Error, WARNING);
    }
  }, [
    userId,
    database,
    activeBranchId,
    dispatch,
    getCountries,
    showNotification,
  ]);

  const storeRoutesHandler = useCallback(() => {
    if (
      routes.length > 0 &&
      routes[routes?.length - 1] !== window.location.href
    ) {
      dispatch(
        setRoutes({
          data: [...routes, window.location.href],
        })
      );
    } else {
      dispatch(
        setRoutes({
          data: [window.location.href],
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.href]);

  const connectListenUser = useCallback(() => {
    if (userId && database) {
      createWSConnection(async () => {
        await invokeWSMethod('ListenUser', database, userId);

        subscribeToWsEvent('OnFrontendUpdate', (response) => {
          const latestVersionTimestamp =
            getLatestFrontendVersionTimestamp(response);

          updateFrontendVersionState(latestVersionTimestamp);
        });
      });
    }
  }, [userId, database, updateFrontendVersionState]);

  // Sidebar calendar day: without a date in the URL it is today, and today
  // must follow the clock in a tab that was opened yesterday.
  const todayMoment = useTodayMoment();
  const selectedDate = useMemo(() => {
    const dateParam = searchParams.get('date');
    return dateParam ? dayjs(dateParam) : dayjs(todayMoment.toDate());
  }, [searchParams, todayMoment]);

  const items: MenuProps['items'] = [
    {
      key: '1',
      label: (
        <span className="unselectable" data-testid="personal-cabinet">
          <UserOutlined style={{ marginRight: '10px' }} />
          Личный кабинет
        </span>
      ),
      onClick: navigateToProfile,
    },
    {
      key: '2',
      label: <Divider className="mg-0" />,
    },
    {
      key: '3',
      label: (
        <span className="unselectable" data-testid="logout">
          <LogoutOutlined style={{ marginRight: '10px' }} />
          Выйти
        </span>
      ),
      onClick: showModal,
    },
  ];

  const setNavigationItems = useCallback(() => {
    const finalBreadCrumbData = {
      title: (
        <div
          className="breadcrumbs-item"
          data-testid={pageBreadcrumbDataTestId}
        >
          {pageTitle}
        </div>
      ),
    };

    const firstBreadCrumbData = {
      title: (
        <Link
          to="/"
          className="breadcrumbs-item"
          data-testid="main-page"
          style={{ color: 'inherit', textDecoration: 'none' }}
        >
          Главная
        </Link>
      ),
    };

    const handleSecondBreadCrumbData = ({
      title,
      path,
      disabled,
      disablingTooltip,
    }: NPage.LevelDataModel): { title: JSX.Element } => {
      if (disabled) {
        return {
          title: (
            <Tooltip title={disablingTooltip}>
              <span className={cn('breadcrumbs-item', { disabled })}>
                {title}
              </span>
            </Tooltip>
          ),
        };
      }

      return {
        title: (
          <Link
            to={path}
            className={cn('breadcrumbs-item', { disabled })}
            style={{ color: 'inherit', textDecoration: 'none' }}
          >
            {title}
          </Link>
        ),
      };
    };

    if (thirdLevelData && secondLevelData) {
      const {
        title: thirdLevelTitle,
        path: thirdLevelPath,
        disabled: isThirdLevelDisabled = false,
        disablingTooltip: thirdLevelTooltip = defaultTooltip,
      } = thirdLevelData;

      const isSendoutReceiversPage = pathname === SENDOUT_RECEIVERS_PATH;

      const handleThirdLevelTitle = (
        isSendoutReceiversPageValue: boolean,
        titleDataValue: string | JSX.Element,
        thirdLevelTitleValue: string | JSX.Element
      ): JSX.Element | string => {
        if (isSendoutReceiversPageValue && titleDataValue) {
          return (
            <div className="sendout-receivers-breadcrumb-title">
              {titleDataValue}
            </div>
          );
        }

        return thirdLevelTitleValue;
      };

      const linkTo = (() => {
        if (isSendoutReceiversPage) {
          const activeSendoutId = searchParams.get('id');
          return `${VIEW_SENDOUTS_PATH}?id=${activeSendoutId}`;
        }
        return thirdLevelPath;
      })();

      const thirdCrumb = isThirdLevelDisabled ? (
        <Tooltip title={thirdLevelTooltip}>
          <span className={cn('breadcrumbs-item', { disabled: true })}>
            {handleThirdLevelTitle(
              isSendoutReceiversPage,
              titleData,
              thirdLevelTitle
            )}
          </span>
        </Tooltip>
      ) : (
        <Link
          to={linkTo}
          className={cn('breadcrumbs-item', { disabled: false })}
          style={{ color: 'inherit', textDecoration: 'none' }}
        >
          {handleThirdLevelTitle(
            isSendoutReceiversPage,
            titleData,
            thirdLevelTitle
          )}
        </Link>
      );

      return [
        firstBreadCrumbData,
        handleSecondBreadCrumbData(secondLevelData),
        { title: thirdCrumb },
        finalBreadCrumbData,
      ];
    }

    if (secondLevelData) {
      return [
        firstBreadCrumbData,
        handleSecondBreadCrumbData(secondLevelData),
        finalBreadCrumbData,
      ];
    }

    return [firstBreadCrumbData, finalBreadCrumbData];
  }, [
    pageBreadcrumbDataTestId,
    pageTitle,
    thirdLevelData,
    secondLevelData,
    searchParams,
    titleData,
    pathname,
  ]);

  const handleClickByMenuItem: NonNullable<MenuProps['onClick']> = useCallback(
    (info) => {
      const domEvent = info.domEvent as MenuDomEvent;

      const hasModifiers =
        domEvent.metaKey ||
        domEvent.ctrlKey ||
        domEvent.shiftKey ||
        domEvent.altKey;

      if (isReactMouseEvent(domEvent)) {
        if (domEvent.button === 1 || hasModifiers) return;
        domEvent.preventDefault();
      } else {
        domEvent.preventDefault();
      }

      const key = String(info.key);
      navigateToPage(key);
      setPageKey(findSidebarParentKey(key));
      if (isMobile) {
        dispatch(setMenuSidebarState(NPage.MenuSidebarState.NONE));
      }
    },
    [navigateToPage, isMobile, dispatch]
  );

  const touchStartX = useRef<number | null>(null);
  const isSwipeHandled = useRef<boolean>(false);

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    touchStartX.current = e.touches[0].clientX;
    isSwipeHandled.current = false;
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!touchStartX.current || isSwipeHandled.current) return;

    const touchEndX = e.touches[0].clientX;
    const swipeDistance = touchEndX - touchStartX.current;

    if (isMobile) {
      if (swipeDistance > 50 && isNone) {
        dispatch(setMenuSidebarState(NPage.MenuSidebarState.FULL));
        isSwipeHandled.current = true;
      } else if (swipeDistance < -50 && !isNone) {
        dispatch(setMenuSidebarState(NPage.MenuSidebarState.NONE));
        isSwipeHandled.current = true;
      }
      return;
    }

    // Проверка на swipe вправо (для открытия меню) и влево (для закрытия меню)
    if (
      swipeDistance > 50 &&
      menuSidebarState === NPage.MenuSidebarState.SMALL
    ) {
      dispatch(setMenuSidebarState(NPage.MenuSidebarState.FULL));
      isSwipeHandled.current = true;
      return;
    }

    if (swipeDistance < -50) {
      if (menuSidebarState === NPage.MenuSidebarState.SMALL) {
        dispatch(setMenuSidebarState(NPage.MenuSidebarState.NONE));
        isSwipeHandled.current = true;
        return;
      }

      if (menuSidebarState === NPage.MenuSidebarState.FULL) {
        dispatch(setMenuSidebarState(NPage.MenuSidebarState.SMALL));
        isSwipeHandled.current = true;
        return;
      }

      return;
    }
  };

  const handleTouchEnd = () => {
    touchStartX.current = null;
    isSwipeHandled.current = false;
  };

  const menuStateButtonClickHandler = () => {
    if (isMobile) {
      dispatch(
        setMenuSidebarState(
          isNone ? NPage.MenuSidebarState.FULL : NPage.MenuSidebarState.NONE
        )
      );
      return;
    }

    if (menuSidebarState === NPage.MenuSidebarState.FULL) {
      dispatch(setMenuSidebarState(NPage.MenuSidebarState.SMALL));
      return;
    }

    dispatch(setMenuSidebarState(NPage.MenuSidebarState.FULL));
  };

  const breadcrumbsItems = useMemo(() => {
    return setNavigationItems();
  }, [setNavigationItems]);

  const onCalendarSelect = useCallback(
    (value: Dayjs) => {
      const date = value.format('YYYY-MM-DD');

      if (pathname === EDIT_EMPLOYEE_PATH) {
        navigate(`/calendar?date=${date}`);
      } else {
        navigatePreserveQueryParams(`/calendar?date=${date}`);
      }

      if (isMobile) {
        dispatch(setMenuSidebarState(NPage.MenuSidebarState.NONE));
      }
    },
    [navigatePreserveQueryParams, pathname, navigate, isMobile, dispatch]
  );

  const openCalendarDialog = useCallback(() => {
    setIsCalendarDialogOpen(true);
  }, []);

  const openSelectBranchModal = useCallback(() => {
    setBranchSelectModal(true);
  }, []);

  const goBackHandler = useCallback(() => {
    const stateGoBackRoute = locationState?.goBackRoute;

    if (stateGoBackRoute) {
      navigate(stateGoBackRoute);
      return;
    }

    goBackRoute ? navigate(goBackRoute) : goBack();
  }, [goBack, navigate, goBackRoute, locationState?.goBackRoute]);

  const handleCalendarDialogClose = useCallback(() => {
    setIsCalendarDialogOpen(false);
  }, []);

  const branchSelectValue = useMemo(
    () => ({
      label: activeBranchName
        ? generateSpanWithDataTestId(activeBranchName, 'active-branch')
        : 'Загрузка...',
      value: 'activeBranchName',
    }),
    [activeBranchName]
  );

  const showCalendar = useMemo(() => {
    return userAccessRights?.Records?.IsJournal && isFull;
  }, [userAccessRights?.Records?.IsJournal, isFull]);

  const showPage = useMemo(() => {
    return hasViewRight(
      pathname,
      navigation as Array<NNavigation.NavigationAllItems>,
      userAccessRights
    );
  }, [userAccessRights, pathname]);

  const isCalendarPage = pathname.includes('/calendar');
  const isClientCardPage = pathname === '/clients/card/';
  const isSchedulePage = pathname.includes('schedule');
  const isProfilePage = pathname === PROFILE_PATH;

  const contentStyle = useMemo(
    () =>
      getContentStyle({
        isCalendarPage,
        isClientCardPage,
        isSchedulePage,
        isProfilePage,
        disableBreadcrumbs,
        colorBgContainer,
        disableContentStyles,
      }),
    [
      isCalendarPage,
      isClientCardPage,
      isSchedulePage,
      isProfilePage,
      disableBreadcrumbs,
      colorBgContainer,
      disableContentStyles,
    ]
  );

  const showFullSidebar = useCallback(() => {
    if (isMobile) {
      dispatch(setMenuSidebarState(NPage.MenuSidebarState.NONE));
    } else {
      dispatch(setMenuSidebarState(NPage.MenuSidebarState.FULL));
    }
  }, [dispatch, isMobile]);

  // Для организации «Бизнес-решения» чат с техподдержкой не показываем
  const isSupportChatVisible = database !== BUSINESS_SOLUTION_ORGANIZATION_ID;

  return {
    pageKey,
    pageTitle,
    memoizedNavigation,
    colorBgContainer,
    userAccessRights,
    photo,
    nameFirst,
    nameLast,
    isMobile,
    titleData,
    pageTitleDataTestId,
    isPageWithExtraTitle,
    isHelpBoxOpened,
    setIsHelpBoxOpened,
    renderCreateBugReportModal,
    openCreateBugReportModal,
    homePageRedirect,
    getCountriesHandler,
    storeRoutesHandler,
    connectListenUser,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    items,
    isNewAppVersion,
    reloadAppWithCurrentUrl,
    selectedDate,
    onCalendarSelect,
    openCalendarDialog,
    handleClickByMenuItem,
    menuStateButtonClickHandler,
    goBackHandler,
    breadcrumbsItems,
    isCalendarDialogOpen,
    handleCalendarDialogClose,
    authenticateHandler,
    isSessionCheckPostponed,
    fetchBranchLightHandler,
    isLogoutModalOpen,
    setIsLogoutModalOpen,
    isBranchSelectModalOpen,
    setBranchSelectModal,
    hideMenuSidebar,
    isSmall,
    isFull,
    isNone,
    openSelectBranchModal,
    branchSelectValue,
    showCalendar,
    showPage,
    menuSidebarState,
    contentStyle,
    isCalendarPage,
    showFullSidebar,
    isLoadingPage,
    setTitleData,
    isAuth,
    isAuthChecking,
    withMenuLinks,
    isSupportChatVisible,
  };
};
